/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Video Popup
(function () {
  function _typeof(obj) {
    return typeof obj;
  }

  function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
      throw new TypeError("Cannot call a class as a function");
    }
  }

  function _defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    Object.defineProperty(Constructor, "prototype", { writable: false });
    return Constructor;
  }

  function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
      throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
      constructor: {
        value: subClass,
        writable: true,
        configurable: true,
      },
    });
    Object.defineProperty(subClass, "prototype", { writable: false });
    if (superClass) _setPrototypeOf(subClass, superClass);
  }

  function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
      var Super = _getPrototypeOf(Derived),
        result;
      if (hasNativeReflectConstruct) {
        var NewTarget = _getPrototypeOf(this).constructor;
        result = Reflect.construct(Super, arguments, NewTarget);
      } else {
        result = Super.apply(this, arguments);
      }
      return _possibleConstructorReturn(this, result);
    };
  }

  function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
      return call;
    } else if (call !== void 0) {
      throw new TypeError(
        "Derived constructors may only return object or undefined"
      );
    }
    return _assertThisInitialized(self);
  }

  function _assertThisInitialized(self) {
    if (self === void 0) {
      throw new ReferenceError(
        "this hasn't been initialised - super() hasn't been called"
      );
    }
    return self;
  }

  function _wrapNativeSuper(Class) {
    var _cache = typeof Map === "function" ? new Map() : undefined;
    _wrapNativeSuper = function _wrapNativeSuper(Class) {
      if (Class === null || !_isNativeFunction(Class)) return Class;
      if (typeof Class !== "function") {
        throw new TypeError(
          "Super expression must either be null or a function"
        );
      }
      if (typeof _cache !== "undefined") {
        if (_cache.has(Class)) return _cache.get(Class);
        _cache.set(Class, Wrapper);
      }
      function Wrapper() {
        return _construct(Class, arguments, _getPrototypeOf(this).constructor);
      }
      Wrapper.prototype = Object.create(Class.prototype, {
        constructor: {
          value: Wrapper,
          enumerable: false,
          writable: true,
          configurable: true,
        },
      });
      return _setPrototypeOf(Wrapper, Class);
    };
    return _wrapNativeSuper(Class);
  }

  function _construct(Parent, args, Class) {
    if (_isNativeReflectConstruct()) {
      _construct = Reflect.construct.bind();
    } else {
      _construct = function _construct(Parent, args, Class) {
        var a = [null];
        a.push.apply(a, args);
        var Constructor = Function.bind.apply(Parent, a);
        var instance = new Constructor();
        if (Class) _setPrototypeOf(instance, Class.prototype);
        return instance;
      };
    }
    return _construct.apply(null, arguments);
  }

  function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
      Boolean.prototype.valueOf.call(
        Reflect.construct(Boolean, [], function () {})
      );
      return true;
    } catch (e) {
      return false;
    }
  }

  function _isNativeFunction(fn) {
    return Function.toString.call(fn).indexOf("[native code]") !== -1;
  }

  function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf
      ? Object.setPrototypeOf.bind()
      : function _setPrototypeOf(o, p) {
          o.__proto__ = p;
          return o;
        };
    return _setPrototypeOf(o, p);
  }

  function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf
      ? Object.getPrototypeOf.bind()
      : function _getPrototypeOf(o) {
          return o.__proto__ || Object.getPrototypeOf(o);
        };
    return _getPrototypeOf(o);
  }

  var VideoThumbnail = /*#__PURE__*/ (function (_HTMLElement) {
    _inherits(VideoThumbnail, _HTMLElement);

    var _super = _createSuper(VideoThumbnail);

    function VideoThumbnail() {
      var _this;

      _classCallCheck(this, VideoThumbnail);

      _this = _super.call(this); // SELECTORS

      _this.popUp = document.querySelector(".video-popup-container");
      _this.popUpVideo = _this.popUp.querySelector(".video-container"); // video inside of popup

      _this.openButton = _this.querySelector(".btn-open");
      _this.closeButton = _this.popUp.querySelector(".btn-close");
      _this.playButton = _this.querySelector(".play-btn"); // ENABLE EXTERNAL POPUP ELEMENT

      if (_this.popUp) {
        document.querySelector("video-popup").setAttribute("enabled", "");
      } // SETUP EVENT LISTENERS

      _this._attachListeners();

      return _this;
    }

    _createClass(VideoThumbnail, [
      {
        key: "videoSrc",
        get: function get() {
          return this.getAttribute("video-src");
        },
      },
      {
        key: "autoPlay",
        get: function get() {
          return this.getAttribute("autoplay");
        },
      },
      {
        key: "controls",
        get: function get() {
          return this.getAttribute("controls");
        },
      },
      {
        key: "_attachListeners",
        value: function _attachListeners() {
          document.addEventListener("keydown", this.closeOnEsc.bind(this));
          this.openButton.addEventListener("click", this.openPopup.bind(this));
          this.playButton.addEventListener("click", this.openPopup.bind(this)); // click outside

          this.popUp.addEventListener("click", this.closePopup.bind(this));
          this.closeButton.addEventListener(
            "click",
            this.closePopup.bind(this)
          );
        },
      },
      {
        key: "openPopup",
        value: function openPopup(e) {
          e.stopPropagation();
          this.createVideo();
          this.popUp.classList.add("open");
          document.documentElement.classList.add("video-popup-open");
        },
      },
      {
        key: "closePopup",
        value: function closePopup(e) {
          e.stopPropagation();
          this.popUpVideo.innerHTML = "";
          this.popUp.classList.remove("open");
          document.documentElement.classList.remove("video-popup-open");
        },
      },
      {
        key: "closeOnEsc",
        value: function closeOnEsc(e) {
          if (e.key === "Escape") {
            this.popUpVideo.innerHTML = "";
            document.documentElement.classList.remove("video-popup-open");
            this.popUp.classList.remove("open");
          }
        },
      },
      {
        key: "createVideo",
        value: function createVideo() {
          var video = document.createElement("video");
          video.src = this.videoSrc;
          /** settings **/

          video.autoplay = true;
          video.controls = true;

          if (this.autoPlay === "false") {
            video.autoplay = false;
          }

          if (this.controls === "false") {
            video.controls = false;
          }

          this.popUpVideo.appendChild(video);
        },
      },
    ]);

    return VideoThumbnail;
  })(/*#__PURE__*/ _wrapNativeSuper(HTMLElement));

  customElements.define("video-thumbnail", VideoThumbnail);

  var VideoPopup = /*#__PURE__*/ (function (_HTMLElement2) {
    _inherits(VideoPopup, _HTMLElement2);

    var _super2 = _createSuper(VideoPopup);

    function VideoPopup() {
      _classCallCheck(this, VideoPopup);

      return _super2.call(this);
    }

    return _createClass(VideoPopup);
  })(_wrapNativeSuper(HTMLElement));

  customElements.define("video-popup", VideoPopup);
})();

// Versand Timer
class WFShippingTimer extends HTMLElement {
  constructor() {
    super();
    this.timerText = document.getElementById("wf-timer-text");
    this.countdown = document.getElementById("wf-countdown");

    document.addEventListener("variant:changed", this.handleVariantChange.bind(this));



  }

  // Versandfertig heisst: Variante ist kaufbar UND liegt wirklich im Lager.
  // Bei Ueberverkauf (Bestand 0, Verkauf erlaubt) ist variant.available zwar true,
  // die Ware geht aber erst nach Wareneingang raus - dann darf kein Timer laufen.
  // Welche Varianten das sind, liefert Liquid in data-versandfertig.
  istVersandfertig(variantId) {
    if (!variantId) return false;
    const liste = (this.getAttribute("data-versandfertig") || "")
      .split(",")
      .filter(Boolean);
    return liste.indexOf(String(variantId)) !== -1;
  }

  handleVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    this.style.display = this.istVersandfertig(variant && variant.id) ? "block" : "none";
}

  connectedCallback() {
    this.targetHour = parseInt(this.getAttribute("data-target-hour"));
    this.targetMinute = parseInt(this.getAttribute("data-target-minute"));
    this.shippingDays = this.getAttribute("data-shipping-days")
      .split(",")
      .map(Number); // e.g., "1,2,3,4,5" for Monday to Friday

    this.updateTimer();
    
    if (this.getAttribute("data-variant-available") == "true") {
      this.style.display= "block";
      this.style.opacity = "1";
  } else if (this.getAttribute("data-variant-available") == "false") {
      this.style.display= "none";
  }
  }

  // Feiertage werden berechnet, nicht gepflegt. Die beweglichen haengen alle am
  // Ostersonntag (Gauss-Formel), die festen stehen im Kalender. Damit stimmt es
  // jedes Jahr von allein, ohne dass jemand eine Liste nachtraegt.
  // Beruecksichtigt sind die gesetzlichen Feiertage in NRW.
  ostersonntag(jahr) {
    const a = jahr % 19;
    const b = Math.floor(jahr / 100);
    const c = jahr % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const monat = Math.floor((h + l - 7 * m + 114) / 31);
    const tag = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(jahr, monat - 1, tag);
  }

  feiertage(jahr) {
    const tage = new Set([
      `${jahr}-01-01`, // Neujahr
      `${jahr}-05-01`, // Tag der Arbeit
      `${jahr}-10-03`, // Tag der Deutschen Einheit
      `${jahr}-11-01`, // Allerheiligen (NRW)
      `${jahr}-12-25`, // 1. Weihnachtstag
      `${jahr}-12-26`, // 2. Weihnachtstag
    ]);

    const ostern = this.ostersonntag(jahr);
    // Abstand zum Ostersonntag in Tagen
    const beweglich = [-2, 1, 39, 50, 60]; // Karfreitag, Ostermontag, Himmelfahrt, Pfingstmontag, Fronleichnam
    beweglich.forEach((abstand) => {
      const t = new Date(ostern);
      t.setDate(t.getDate() + abstand);
      tage.add(this.alsSchluessel(t));
    });

    return tage;
  }

  alsSchluessel(datum) {
    const m = String(datum.getMonth() + 1).padStart(2, "0");
    const t = String(datum.getDate()).padStart(2, "0");
    return `${datum.getFullYear()}-${m}-${t}`;
  }

  // Zusaetzliche Schliesstage aus den Theme-Einstellungen.
  // Zwei Schreibweisen erlaubt: "24.12." gilt jedes Jahr, "2026-12-28" nur einmal.
  istSchliesstag(datum) {
    const roh = this.getAttribute("data-schliesstage") || "";
    if (!roh.trim()) return false;
    const m = String(datum.getMonth() + 1).padStart(2, "0");
    const t = String(datum.getDate()).padStart(2, "0");
    const jaehrlich = `${t}.${m}.`;
    const einmalig = this.alsSchluessel(datum);
    return roh
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean)
      .some((e) => e === einmalig || e.replace(/\.$/, "") + "." === jaehrlich);
  }

  istVersandtag(datum) {
    if (!this.shippingDays.includes(datum.getDay())) return false;
    if (this.istSchliesstag(datum)) return false;
    const jahr = datum.getFullYear();
    if (!this.feiertagsCache || this.feiertagsCache.jahr !== jahr) {
      this.feiertagsCache = { jahr: jahr, tage: this.feiertage(jahr) };
    }
    return !this.feiertagsCache.tage.has(this.alsSchluessel(datum));
  }

  updateTimer() {
    const now = new Date();
    let targetTime = new Date();
    targetTime.setHours(this.targetHour, this.targetMinute, 0, 0);

    // Heute kein Versandtag (Wochenende, Feiertag, Schliesstag): auf den naechsten rollen
    if (!this.istVersandtag(now)) {
      do {
        targetTime.setDate(targetTime.getDate() + 1);
      } while (!this.istVersandtag(targetTime));
    } else if (now >= targetTime) {
      // Versandtag, aber Annahmeschluss vorbei
      do {
        targetTime.setDate(targetTime.getDate() + 1);
      } while (!this.istVersandtag(targetTime));
    }

    const remainingTime = targetTime - now;

    // Calculate hours, minutes, and seconds
    const hours = Math.floor(remainingTime / (1000 * 60 * 60));
    const minutes = Math.floor(
      (remainingTime % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

    // Buy Box C: „Versand morgen, wenn du in 16 Std. 8 Min. bestellst“ – ohne Ausrufezeichen.
    // Unter 1 Stunde keine „0 Std.“, dafür laufende Sekunden („4 Min. 07 Sek.“), unter 1 Minute nur „37 Sek.“.
    // Ab 1 Stunde Sekunden nur mit Schalter „Sekunden zeigen“ (zweistellig, damit die Zeile nicht springt)
    const fdC = !!this.closest(".fd-bb-c");
    const fdSek = this.hasAttribute("data-fd-sekunden");
    const zwei = (n) => String(n).padStart(2, "0");
    this.countdown.innerText = !fdC
      ? `${hours}hr ${minutes}min ${seconds}sec`
      : hours === 0
        ? minutes === 0
          ? `${seconds} Sek.`
          : `${minutes} Min. ${zwei(seconds)} Sek.`
        : fdSek
          ? `${hours} Std. ${zwei(minutes)} Min. ${zwei(seconds)} Sek.`
          : `${hours} Std. ${minutes} Min.`;

    // Update the timer text based on the next shipping day
    const today = new Date().setHours(0, 0, 0, 0);
    const targetDate = new Date(targetTime).setHours(0, 0, 0, 0);

    if (targetDate === today) {
      this.timerText.innerText = fdC ? "Versand heute" : "Versand heute!";
    } else if (targetDate === today + 86400000) {
      // 86400000 ms = 1 day
      this.timerText.innerText = fdC ? "Versand morgen" : "Versand morgen!";
    } else {
      const nextShippingDay = targetTime.toLocaleDateString("de-DE", {
        weekday: "long",
      });
      this.timerText.innerText = `Versand am ${
        nextShippingDay.charAt(0).toUpperCase() + nextShippingDay.slice(1)
      }${fdC ? "" : "!"}`;
    }

    setTimeout(() => this.updateTimer(), 1000);
  }
}

customElements.define("wf-shipping-timer", WFShippingTimer);

class WFCountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null;
  }

  connectedCallback() {
    const startDate = new Date(this.getAttribute("data-start")).getTime();
    const endDate = new Date(this.getAttribute("data-end")).getTime();
    const now = new Date().getTime();

    const timerContainer = this.querySelector(".wf-countdown-timer__container");

    if (now < startDate) {
      timerContainer.innerHTML = `<p>Der Countdown beginnt bald!</p>`;
      this.expandContainer(timerContainer);
      return;
    }

    if (now >= endDate) {
      timerContainer.innerHTML = `<p>Der Countdown ist abgelaufen!</p>`;
      this.expandContainer(timerContainer);
      setTimeout(() => this.collapseContainer(timerContainer), 3000);
      return;
    }

    this.render();

    this.timerInterval = setInterval(
      () => this.updateTimer(endDate, timerContainer),
      1000
    );
    setTimeout(() => this.expandContainer(timerContainer), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timerInterval);
  }

  render() {
    const timerContainer = this.querySelector("#wf-countdown");
    if (!timerContainer) return;

    timerContainer.innerHTML = `
      <span class="wf-countdown__time" id="days">00</span>
      <span class="wf-countdown__label">Tage</span>
      <span class="wf-countdown__time" id="hours">00</span>
      <span class="wf-countdown__label">Std</span>
      <span class="wf-countdown__time" id="minutes">00</span>
      <span class="wf-countdown__label">Min</span>
      <span class="wf-countdown__time" id="seconds">00</span>
      <span class="wf-countdown__label">Sek</span>
    `;
  }

  updateTimer(endDate, timerContainer) {
    const now = new Date().getTime();
    const distance = endDate - now;

    if (distance < 0) {
      clearInterval(this.timerInterval);
      setTimeout(() => this.collapseContainer(timerContainer), 3000);
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    this.querySelector("#days").textContent = days.toString().padStart(2, "0");
    this.querySelector("#hours").textContent = hours
      .toString()
      .padStart(2, "0");
    this.querySelector("#minutes").textContent = minutes
      .toString()
      .padStart(2, "0");
    this.querySelector("#seconds").textContent = seconds
      .toString()
      .padStart(2, "0");
  }

  expandContainer(container) {
    container.classList.add("active");
    container.classList.remove("collapsed");
  }

  collapseContainer(container) {
    container.classList.remove("active");
    container.classList.add("collapsed");
  }
}

customElements.define("wf-countdown-timer", WFCountdownTimer);

class WFAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.announcements = this.querySelectorAll(".wf-announcement-item");
    this.nextButton = this.querySelector(".wf-announcement-next");
    this.prevButton = this.querySelector(".wf-announcement-prev");
    this.currentIndex = 0;
    this.delay = this.getAttribute("data-delay");
    this.autoRotate = this.getAttribute("data-auto-rotate");
    this.interval = null;
  }

  connectedCallback() {
    this.updateAnnouncementDisplay();
    this.setupEventListeners();
    if (this.autoRotate === "true") {
      this.startAutoRotate();
    }
  }

  updateAnnouncementDisplay() {
    this.announcements.forEach((item, idx) => {
      item.classList.remove("active");
      if (idx === this.currentIndex) {
        item.classList.add("active");
      }
    });
  }

  nextAnnouncement() {
    this.announcements[this.currentIndex].style.transform = "translateY(100%)";
    this.currentIndex = (this.currentIndex + 1) % this.announcements.length;
    this.announcements[this.currentIndex].style.transform = "translateY(0)";
    this.updateAnnouncementDisplay();
  }

  prevAnnouncement() {
    this.announcements[this.currentIndex].style.transform = "translateY(-100%)";
    this.currentIndex =
      (this.currentIndex - 1 + this.announcements.length) %
      this.announcements.length;
    this.announcements[this.currentIndex].style.transform = "translateY(0)";
    this.updateAnnouncementDisplay();
  }

  resetInterval() {
    if (this.autoRotate === "true") {
      clearInterval(this.interval);
      this.startAutoRotate();
    }
  }

  startAutoRotate() {
    this.interval = setInterval(() => this.nextAnnouncement(), this.delay);
  }

  stopAutoRotate() {
    clearInterval(this.interval);
  }

  setupEventListeners() {
    this.nextButton.addEventListener("click", () => {
      this.nextAnnouncement();
      this.resetInterval();
    });

    this.prevButton.addEventListener("click", () => {
      this.prevAnnouncement();
      this.resetInterval();
    });
  }

  disconnectedCallback() {
    this.stopAutoRotate();
    this.nextButton.removeEventListener("click", this.nextAnnouncement);
    this.prevButton.removeEventListener("click", this.prevAnnouncement);
  }
}

customElements.define("wf-announcement-bar", WFAnnouncementBar);

class CardSlider extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.tabParent = this.closest("product-list");
    if (this.tabParent && this.tabParent.getAttribute("id")) {
      this.triggerButton = document
      .querySelector(`[aria-controls="${this.tabParent.getAttribute("id")}"]`)
      ?.closest("tabs-nav") ?? null;
    }

    if (this.triggerButton) {
      //console.log(this.triggerButton);
      this.triggerButton.addEventListener(
        "tabs-nav:changed",
        this.handleTabChanged.bind(this)
      );
    }
    //this.addEventListener('click', this.handleClick.bind(this));
  }

  handleTabChanged(e) {
    //console.log(e, this.flickity);
    this.flickity.destroy();
    this.initFlickity();
  }

  initFlickity() {
    this.flickity = new Flickity(this.element, {
      pageDots: true,
      prevNextButtons: false,
      wrapAround: false,
      cellAlign: "left",
      contain: true,
      adaptiveHeight: false,
      setGallerySize: false
    });
  }

  connectedCallback() {
    this.initFlickity();
    this.checkItem();
  }

  // Delay function using Promise
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async checkItem() {
    // Checks if it's the first Item in a grid and on mobile.
    // If yes, a small animation is played to signalize the slider
    this.parent = this.closest(".product-list__inner");
    if (this.parent) {
      if (this.parent.firstElementChild === this.closest("product-item")) {
        await this.delay(1000);
        let slider = this.flickity.element?.querySelector(".flickity-slider");
        if (slider) {
          slider.classList.add("transition-flickity");
          slider.classList.add("transition-flickity-animation");
          await this.delay(700); // Delay for 700ms before removing class
          slider.classList.remove("transition-flickity-animation");
          await this.delay(700); // Delay for 700ms before removing class
          slider.classList.remove("transition-flickity");
        }
      }
    }
  }
}


document.addEventListener("DOMContentLoaded", () => {
  window.customElements.define("card-slider", CardSlider);
});

